import React, { useRef, useState } from "react";

/**
 * The keyboard IS the transmission. Button count = "speed" (4/8/12/16-speed).
 * www. is always the first button (top-left), .com the last (bottom-right).
 * Only the characters on the track are custom-fitted for the vehicle.
 *
 * Keys render in the SAME monospace font as the track so look-alike glyphs
 * (0/O, 1/l/I, u/v) are never ambiguous across devices. When a press is wrong
 * (onPress returns false) that key flashes red as immediate per-key feedback.
 */
export default function RacingKeyboard({ keys, onPress, nextToken, disabled }) {
  const cols = Math.max(2, Math.round(keys.length / 4));
  const [wrong, setWrong] = useState(null); // { i, id }
  const timer = useRef(null);

  const label = (k) => (k === "space" ? "␣" : k);
  const isHot = (k) => {
    if (!nextToken) return false;
    if (k === "space") return nextToken === " ";
    return k.toLowerCase() === String(nextToken).toLowerCase();
  };
  const testid = (k) =>
    k === "www." ? "key-www" : k === ".com" ? "key-com" : k === "space" ? "key-space" : `key-${k}`;

  const handle = (k, i) => {
    if (disabled) return;
    const res = onPress(k);
    if (res === false) {
      const id = Date.now();
      setWrong({ i, id });
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setWrong((w) => (w && w.id === id ? null : w)), 400);
    }
  };

  return (
    <div
      className="w-full grid gap-2"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      data-testid="racing-keyboard"
    >
      {keys.map((k, i) => {
        const special = k === "www." || k === ".com";
        const hot = isHot(k);
        const isWrong = wrong && wrong.i === i;
        return (
          <button
            key={`${k}-${i}`}
            type="button"
            disabled={disabled}
            data-testid={testid(k)}
            data-wrong={isWrong ? "1" : undefined}
            onPointerDown={(e) => {
              e.preventDefault();
              handle(k, i);
            }}
            className={`select-none min-h-[54px] rounded-md border-2 font-mono font-bold tabular-nums flex items-center justify-center
              shadow-[0_3px_0_rgba(0,0,0,0.3)]
              transition-transform duration-75 active:scale-90 active:translate-y-[2px] disabled:opacity-50
              ${special ? "bg-neutral-100 text-neutral-900 text-lg border-neutral-500" : "bg-white text-neutral-900 text-2xl border-neutral-500"}
              ${hot ? "!border-race-red ring-2 ring-race-red bg-red-50 shadow-[0_0_16px_rgba(255,42,0,0.35)]" : "hover:border-neutral-700"}
              ${isWrong ? "!bg-race-red !text-white !border-race-red animate-key-error" : ""}`}
          >
            {label(k)}
          </button>
        );
      })}
    </div>
  );
}
